/**
 * Customer Videos Section
 * Gère la lecture des vidéos avec autoplay muet et reprise au clic
 */

class CustomerVideosSection {
  constructor(container) {
    this.container = container;
    this.wrapper = container.querySelector('[data-customer-videos]');
    if (!this.wrapper) return;

    this.layout = this.wrapper.dataset.layout;
    this.hoverPlayDesktop = this.wrapper.dataset.hoverPlayDesktop === 'true';
    this.autoplayAll = this.wrapper.dataset.autoplayAll === 'true';
    this.cards = container.querySelectorAll('[data-video-card]');
    this.currentlyPlaying = null;

    this.init();
  }

  init() {
    this.cards.forEach((card) => {
      const video = card.querySelector('[data-video-player]');
      const toggleBtn = card.querySelector('[data-video-toggle]');

      if (!video) return;

      // Si autoplay global est activé, démarrer la vidéo en muet
      if (this.autoplayAll) {
        this.startAutoplay(video);
      }

      // Événement de clic sur le bouton toggle
      if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleVideoClick(video, card);
        });
      }

      // Événement de clic sur la vidéo elle-même
      video.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleVideoClick(video, card);
      });

      // Hover play sur desktop
      if (this.hoverPlayDesktop && window.matchMedia('(min-width: 750px)').matches) {
        this.setupHoverPlay(card, video);
      }

      // Si autoplay global, gérer le hover pour pause/play des autres vidéos
      if (this.autoplayAll && window.matchMedia('(min-width: 750px)').matches) {
        this.setupAutoplayHover(card, video);
      }

      // Mettre à jour l'état des icônes quand la vidéo se termine
      video.addEventListener('ended', () => {
        this.updateButtonState(card, false);
      });

      // Mettre à jour l'état quand la vidéo joue/pause
      video.addEventListener('play', () => {
        this.updateButtonState(card, true);
      });

      video.addEventListener('pause', () => {
        this.updateButtonState(card, false);
      });
    });

    // Observer pour autoplay quand visible (Intersection Observer)
    if (this.autoplayAll) {
      this.setupVisibilityObserver();
    }
  }

  startAutoplay(video) {
    // S'assurer que la vidéo est muette pour l'autoplay
    video.muted = true;
    video.play().catch(() => {
      // Autoplay bloqué par le navigateur, on ne fait rien
    });
  }

  handleVideoClick(video, card) {
    const wasPlaying = !video.paused;
    const wasMuted = video.muted;

    // Si la vidéo jouait en autoplay (muet), on la redémarre depuis le début avec le son
    if (wasPlaying && wasMuted) {
      video.pause();
      video.currentTime = 0;
      video.muted = false;

      // Pause des autres vidéos
      this.pauseOtherVideos(video);

      video.play().then(() => {
        this.currentlyPlaying = video;
        this.updateButtonState(card, true);
      }).catch(() => {
        // Lecture bloquée
      });
    }
    // Si la vidéo jouait avec le son, on la met en pause
    else if (wasPlaying && !wasMuted) {
      video.pause();
      this.currentlyPlaying = null;
      this.updateButtonState(card, false);

      // Si autoplay global, reprendre en muet
      if (this.autoplayAll) {
        video.muted = true;
        video.play().catch(() => {});
      }
    }
    // Si la vidéo était en pause, on la démarre depuis le début avec le son
    else {
      video.currentTime = 0;
      video.muted = false;

      // Pause des autres vidéos
      this.pauseOtherVideos(video);

      video.play().then(() => {
        this.currentlyPlaying = video;
        this.updateButtonState(card, true);
      }).catch(() => {
        // Lecture bloquée
      });
    }
  }

  pauseOtherVideos(currentVideo) {
    this.cards.forEach((card) => {
      const video = card.querySelector('[data-video-player]');
      if (video && video !== currentVideo) {
        video.pause();

        // Si autoplay global, reprendre en muet
        if (this.autoplayAll) {
          video.muted = true;
          video.play().catch(() => {});
        }

        this.updateButtonState(card, false);
      }
    });
  }

  setupHoverPlay(card, video) {
    // Ne pas activer hover play si autoplay global est actif (géré par setupAutoplayHover)
    if (this.autoplayAll) return;

    card.addEventListener('mouseenter', () => {
      if (this.currentlyPlaying !== video) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });

    card.addEventListener('mouseleave', () => {
      if (this.currentlyPlaying !== video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  setupAutoplayHover(card, video) {
    card.addEventListener('mouseenter', () => {
      // Mettre en pause toutes les autres vidéos (sauf celle avec le son)
      this.cards.forEach((otherCard) => {
        const otherVideo = otherCard.querySelector('[data-video-player]');
        if (otherVideo && otherVideo !== video && otherVideo !== this.currentlyPlaying) {
          otherVideo.pause();
        }
      });

      // S'assurer que la vidéo survolée joue (en muet si pas la vidéo active)
      if (this.currentlyPlaying !== video) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });

    card.addEventListener('mouseleave', () => {
      // Au mouseleave, reprendre l'autoplay de toutes les vidéos visibles
      this.resumeAllAutoplay();
    });
  }

  resumeAllAutoplay() {
    this.cards.forEach((card) => {
      const video = card.querySelector('[data-video-player]');
      if (video && video !== this.currentlyPlaying) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });
  }

  setupVisibilityObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('[data-video-player]');
        if (!video) return;

        if (entry.isIntersecting) {
          // Si pas de vidéo en cours avec le son, jouer en muet
          if (this.currentlyPlaying !== video) {
            video.muted = true;
            video.play().catch(() => {});
          }
        } else {
          // Si la vidéo n'est pas celle en cours avec le son, la mettre en pause
          if (this.currentlyPlaying !== video) {
            video.pause();
          }
        }
      });
    }, options);

    this.cards.forEach((card) => {
      observer.observe(card);
    });
  }

  updateButtonState(card, isPlaying) {
    const toggleBtn = card.querySelector('[data-video-toggle]');
    const playIcon = card.querySelector('[data-icon-play]');
    const pauseIcon = card.querySelector('[data-icon-pause]');
    const video = card.querySelector('[data-video-player]');

    if (toggleBtn) {
      // On considère "playing" seulement si la vidéo joue avec le son
      const isActivelyPlaying = isPlaying && video && !video.muted;
      toggleBtn.setAttribute('aria-pressed', isActivelyPlaying ? 'true' : 'false');
    }

    if (playIcon && pauseIcon) {
      const isActivelyPlaying = isPlaying && video && !video.muted;
      playIcon.style.display = isActivelyPlaying ? 'none' : 'flex';
      pauseIcon.style.display = isActivelyPlaying ? 'flex' : 'none';
    }
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.customer-videos-section').forEach((section) => {
    new CustomerVideosSection(section);
  });
});

// Support pour Shopify Section rendering
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.customer-videos-section');
    if (section) {
      new CustomerVideosSection(section);
    }
  });
}
